
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from "@angular/core";
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { CommonService } from 'huddyapi';
import { ErrorSnackBar } from '../components/error-snack-bar/error-snack-bar.component';
import { Util } from '../components/util';

type ErrorMessage = string | ((error?: any, context?: string) => string);

type ErrorMessageSet = {
  default?: ErrorMessage,
  handled?: (error?: any, context?: string) => void,
  [code: number]: ErrorMessage
};

@Injectable()
export class DesktopErrorHandler extends ErrorHandler {
  static buildStandardErrorMessage(baseText: string, contextJoinText?: string, defaultContext?: string): ErrorMessage {
    return (error?: any, context: string = defaultContext): string => {
      return baseText + (context ? (' ' + (contextJoinText ? (contextJoinText + ' ') : '') + context) : '')
    };
  }

  static MESSAGE_SILENT: string = '__MESSAGE_SILENT__';

  static NO_INTERNET_MESSAGES: ErrorMessageSet = {
    0: 'Could not reach server'
  }

  static COMMON_MESSAGES: ErrorMessageSet = {
    ...DesktopErrorHandler.NO_INTERNET_MESSAGES,
    default: DesktopErrorHandler.buildStandardErrorMessage('Something went wrong', 'in'),
    400: DesktopErrorHandler.buildStandardErrorMessage('Error', 'in'),
    401: DesktopErrorHandler.buildStandardErrorMessage('Error', 'in'),
    403: DesktopErrorHandler.buildStandardErrorMessage('Error', 'in'),
    404: DesktopErrorHandler.buildStandardErrorMessage('Could not find', null, 'data'),
    406: DesktopErrorHandler.buildStandardErrorMessage('Error', 'in'),
    409: DesktopErrorHandler.buildStandardErrorMessage('Conflict', 'in'),
    500: DesktopErrorHandler.buildStandardErrorMessage('Error', 'in')
  };

  static NAVIGATION_MESSAGES: ErrorMessageSet = {
    default: DesktopErrorHandler.buildStandardErrorMessage('Error navigating')
  }

  static SEARCH_MESSAGES: ErrorMessageSet = {
    default: DesktopErrorHandler.buildStandardErrorMessage('Error searching', 'for', 'data')
  }

  static REQUEST_METHOD_MESSAGE_SETS: { [method: string]: ErrorMessageSet } = {
    GET: {
      ...DesktopErrorHandler.NO_INTERNET_MESSAGES,
      default: DesktopErrorHandler.buildStandardErrorMessage('Error getting', null, 'data'),
      404: DesktopErrorHandler.buildStandardErrorMessage('Could not find', null, 'data')
    },
    POST: {
      ...DesktopErrorHandler.NO_INTERNET_MESSAGES,
      default: DesktopErrorHandler.buildStandardErrorMessage('Error saving', null, 'data'),
      409: DesktopErrorHandler.buildStandardErrorMessage('Conflict saving', null, 'data')
    },
    PUT: {
      ...DesktopErrorHandler.NO_INTERNET_MESSAGES,
      default: DesktopErrorHandler.buildStandardErrorMessage('Error updating', null, 'data'),
      404: DesktopErrorHandler.buildStandardErrorMessage('Could not find existing', null, 'data'),
      409: DesktopErrorHandler.buildStandardErrorMessage('Conflict updating', null, 'data')
    },
    DELETE: {
      ...DesktopErrorHandler.NO_INTERNET_MESSAGES,
      default: DesktopErrorHandler.buildStandardErrorMessage('Error deleting', null, 'data'),
      404: DesktopErrorHandler.buildStandardErrorMessage('Could not find existing', null, 'data')
    }
  }

  constructor(
    private snackBar: MatSnackBar
  ) {
    super();
  }

  handleError(error: any): void {
    console.error('UNHANDLED', error);
  }

  handle(error: any, context?: string, messages?: ErrorMessageSet): void {
    if(error) {
      if(error instanceof HttpErrorResponse) {
        if(error.headers.get('x-handled') == 'true') {
          if(messages.handled)
            messages.handled(error, context);
          return;
        }
        if(error.status == null)
          return this.displayError(this.getErrorMessage(messages, error, context), error);
        const requestMethod: string = error.headers.get('x-request-method');
        return this.displayError(this.getErrorMessage(messages, error, context, error.status, requestMethod), error);
      }
      if(typeof error == 'string')
        return this.displayError(error);
    }
    return this.displayError(this.getErrorMessage(messages, error, context), error);
  }

  handleSimple(error: any, message: string): void {
    this.handle(error, null, { ...DesktopErrorHandler.NO_INTERNET_MESSAGES, default: message });
  }

  private evaluateErrorMessage(errorMessage: ErrorMessage, error?: any, context?: string): string {
    if(!errorMessage)
      return null;
    if(typeof errorMessage == 'string')
      return errorMessage;
    return errorMessage(error, context);
  }

  private getErrorMessage(messages: ErrorMessageSet, error?: any, context?: string, key?: number, requestMethod?: string): string {
    let message: string;
    if(messages)
      message = this.evaluateErrorMessage((key == null) ? messages.default : (messages[key] || messages.default), error, context);
    if(!message) {
      let fallback: ErrorMessageSet;
      if(requestMethod)
        fallback = DesktopErrorHandler.REQUEST_METHOD_MESSAGE_SETS[requestMethod] || DesktopErrorHandler.COMMON_MESSAGES;
      else
        fallback = DesktopErrorHandler.COMMON_MESSAGES;
      if(fallback)
        message = this.evaluateErrorMessage((key == null) ? fallback.default : (fallback[key] || fallback.default), error, context);
    }
    return message || 'Something went wrong';
  }

  private displayError(message: string, error?: any): void {
    let detail: string = '';
    if(message && message != DesktopErrorHandler.MESSAGE_SILENT) {
      detail += Util.version;
      const user: any = CommonService.getUser();
      if(user && user.id)
        detail += (detail ? ' ' : '') + user.id.toString();
      if(error && (error instanceof HttpErrorResponse)) {
        let statusStr: string = '';
        if(error.status != null)
          statusStr += error.status.toString();
        const requestMethod: string = error.headers.get('x-request-method');
        if(requestMethod)
          statusStr += (statusStr ? ' ' : '') + requestMethod;
        const elapsed: string = error.headers.get('x-elapsed-ms');
        if(elapsed)
          statusStr += (statusStr ? ' ' : '') + elapsed + 'ms';
        if(statusStr)
          detail += (detail ? ' ' : '') + '(' + statusStr + ')';
        const dateReceived: string = error.headers.get('x-date-received');
        if(dateReceived)
          detail += (detail ? ' ' : '') + 'at ' + dateReceived;
        if(error.url)
          detail += (detail ? ' ' : '') + '(' + error.url + ')';
      }
      this.snackBar.openFromComponent(ErrorSnackBar, {
        data: {
          message: message,
          detail: detail
        }
      });
    }
    if(error)
      console.error((message ? (message + ' - ' + detail) : 'No message') + ':', error);
  }
}